<template>
  <div class="mot-panel">
    <MaintenanceOSInstanceDetail
      v-if="selectedOsId"
      :os-id="selectedOsId"
      :access-level="accessLevel"
      @back="selectedOsId = ''"
      @changed="reload"
    />

    <template v-else>
      <div v-if="accessLevel >= 2" class="mot-top">
        <button class="mot-btn mot-btn-primary" type="button" @click="showCreateForm = !showCreateForm">
          {{ showCreateForm ? 'Fechar formulário' : '+ Requisitar OS' }}
        </button>

        <div v-if="showCreateForm" class="mot-new-form">
          <div class="mot-grid">
            <div class="mot-field mot-field-wide">
              <label class="mot-label">Título da OS</label>
              <input class="mot-input" type="text" v-model="createForm.title" placeholder="Ex.: Manutenção elétrica — bloco B" />
            </div>
            <div class="mot-field">
              <label class="mot-label">Prioridade</label>
              <select class="mot-select" v-model="createForm.priority">
                <option value="baixa">Baixa</option>
                <option value="normal">Normal</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
            <div class="mot-field">
              <label class="mot-label">Prazo</label>
              <input class="mot-input" type="date" v-model="createForm.dueDate" />
            </div>
            <div class="mot-field mot-field-wide">
              <label class="mot-label">Descrição</label>
              <textarea class="mot-textarea" v-model="createForm.description" rows="2"></textarea>
            </div>
            <div class="mot-field mot-field-wide">
              <label class="mot-label">Responsáveis <span class="mot-hint">— o primeiro que concluir um item já vale para todos</span></label>
              <select class="mot-select" v-model="respPick" @change="addResponsavel">
                <option value="">— Adicionar responsável —</option>
                <option v-for="u in userOptions" :key="u.username" :value="u.username">{{ u.name }}</option>
              </select>
              <div v-if="createForm.responsavelUserIds.length" class="mot-chips">
                <span v-for="uid in createForm.responsavelUserIds" :key="uid" class="mot-chip">
                  {{ userNameOf(uid) }}
                  <button class="mot-chip-x" type="button" @click="removeResponsavel(uid)">×</button>
                </span>
              </div>
            </div>
          </div>

          <div class="mot-items-form">
            <div class="mot-section-label">Rotinas desta OS</div>
            <div v-for="(item, idx) in createForm.items" :key="idx" class="mot-item-row">
              <input class="mot-input" type="text" v-model="item.title" placeholder="Título da rotina" />
              <input class="mot-input" type="text" v-model="item.description" placeholder="Descrição (opcional)" />
              <button class="mot-chip-x" type="button" @click="removeCreateItem(idx)">×</button>
            </div>
            <button class="mot-btn" type="button" @click="addCreateItem">+ Adicionar rotina</button>
          </div>

          <p v-if="createError" class="mot-error">{{ createError }}</p>
          <div class="mot-form-actions">
            <button class="mot-btn mot-btn-primary" type="button" :disabled="creating" @click="submitCreate">
              {{ creating ? 'Requisitando...' : 'Requisitar OS' }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="loading" class="mot-loading">Carregando...</div>
      <div v-else-if="typeGroups.length === 0" class="mot-empty">Nenhuma OS encontrada para este projeto.</div>
      <div v-else class="mot-list">
        <div v-for="group in typeGroups" :key="group.rootId" class="mot-type-card">
          <div class="mot-type-header">
            <div class="mot-type-heading">
              <div class="mot-type-title">{{ group.title }}</div>
              <div class="mot-type-sub">{{ group.instances.length }} {{ group.instances.length > 1 ? 'instâncias' : 'instância' }}</div>
            </div>
            <div class="mot-type-actions">
              <button v-if="accessLevel >= 2" class="mot-btn" type="button" @click="toggleEditType(group)">
                {{ editingType === group.rootId ? 'Fechar' : 'Editar' }}
              </button>
              <button v-if="accessLevel >= 2" class="mot-btn mot-btn-primary" type="button" @click="toggleDesignateType(group.rootId)">
                {{ designatingType === group.rootId ? 'Fechar' : 'Requisitar OS' }}
              </button>
            </div>
          </div>

          <div class="mot-type-status">
            <span v-if="group.lastCompletedAt">Preenchida pela última vez em {{ formatDateTime(group.lastCompletedAt) }}{{ group.lastCompletedByName ? ` por ${group.lastCompletedByName}` : '' }}</span>
            <span v-else>Ainda não preenchida</span>
            <span v-if="group.newestHasPending" class="mot-flag mot-flag-pending">⚠ Pendente de preenchimento</span>
            <span v-else-if="group.newestApproved" class="mot-flag mot-flag-ok">✓ Aprovada</span>
            <span v-else class="mot-flag">Aguardando revisão</span>
          </div>

          <div class="mot-type-instances">
            <button v-for="inst in group.instances" :key="inst.id" class="mot-instance-chip" type="button" @click="selectedOsId = inst.id">
              OS{{ inst.osNumber }}
              <span class="mot-status-badge" :class="'s-' + inst.status">{{ statusLabel(inst.status) }}</span>
            </button>
          </div>

          <div v-if="editingType === group.rootId" class="mot-new-form">
            <div class="mot-grid">
              <div class="mot-field mot-field-wide">
                <label class="mot-label">Título</label>
                <input class="mot-input" type="text" v-model="editTypeForm.title" />
              </div>
              <div class="mot-field">
                <label class="mot-label">Prioridade</label>
                <select class="mot-select" v-model="editTypeForm.priority">
                  <option value="baixa">Baixa</option>
                  <option value="normal">Normal</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
              <div class="mot-field">
                <label class="mot-label">Prazo</label>
                <input class="mot-input" type="date" v-model="editTypeForm.dueDate" />
              </div>
              <div class="mot-field mot-field-wide">
                <label class="mot-label">Descrição</label>
                <textarea class="mot-textarea" v-model="editTypeForm.description" rows="2"></textarea>
              </div>
            </div>
            <div class="mot-form-actions">
              <button class="mot-btn mot-btn-primary" type="button" @click="submitEditType(group.rootId)">Salvar</button>
            </div>
          </div>

          <div v-if="designatingType === group.rootId" class="mot-new-form">
            <div class="mot-grid">
              <div class="mot-field mot-field-wide">
                <label class="mot-label">Responsáveis</label>
                <select class="mot-select" v-model="designatePick" @change="addDesignateResponsavel">
                  <option value="">— Adicionar responsável —</option>
                  <option v-for="u in userOptions" :key="u.username" :value="u.username">{{ u.name }}</option>
                </select>
                <div v-if="designateForm.responsavelUserIds.length" class="mot-chips">
                  <span v-for="uid in designateForm.responsavelUserIds" :key="uid" class="mot-chip">
                    {{ userNameOf(uid) }}
                    <button class="mot-chip-x" type="button" @click="removeDesignateResponsavel(uid)">×</button>
                  </span>
                </div>
              </div>
              <div class="mot-field">
                <label class="mot-label">Prazo</label>
                <input class="mot-input" type="date" v-model="designateForm.dueDate" />
              </div>
              <div class="mot-field mot-field-wide">
                <label class="mot-check-row">
                  <input type="checkbox" v-model="designateForm.requireAll" />
                  Todos precisam preencher (cria uma OS separada por pessoa)
                </label>
              </div>
            </div>
            <p v-if="designateError" class="mot-error">{{ designateError }}</p>
            <p v-if="designateResult" class="mot-success">{{ designateResult }}</p>
            <div class="mot-form-actions">
              <button class="mot-btn mot-btn-primary" type="button" :disabled="designating" @click="submitDesignate(group.rootId)">
                {{ designating ? 'Requisitando...' : 'Requisitar OS' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref, type PropType } from 'vue'
import { maintenanceStore, type ServiceOrder } from '@/store/maintenance'
import { accountsStore } from '@/store/accounts'
import type { Project } from '@/store/projects'
import MaintenanceOSInstanceDetail from './MaintenanceOSInstanceDetail.vue'

interface OsTypeGroup {
  rootId: string
  title: string
  instances: ServiceOrder[]
  lastCompletedAt: string
  lastCompletedByName: string
  newestHasPending: boolean
  newestApproved: boolean
}

export default defineComponent({
  name: 'MaintenanceOSTiposTab',
  components: { MaintenanceOSInstanceDetail },
  props: {
    project: { type: Object as PropType<Project>, required: true },
    accessLevel: { type: Number, default: 0 }
  },
  setup(props) {
    const loading = ref(true)
    const selectedOsId = ref('')

    const showCreateForm = ref(false)
    const creating = ref(false)
    const createError = ref('')
    const respPick = ref('')
    const createForm = reactive({
      title: '', description: '', priority: 'normal' as 'baixa' | 'normal' | 'alta' | 'urgente',
      dueDate: '', responsavelUserIds: [] as string[], items: [{ title: '', description: '' }]
    })

    const editingType = ref('')
    const editTypeForm = reactive({ title: '', description: '', priority: 'normal', dueDate: '' })

    const designatingType = ref('')
    const designatePick = ref('')
    const designating = ref(false)
    const designateError = ref('')
    const designateResult = ref('')
    const designateForm = reactive({ responsavelUserIds: [] as string[], dueDate: '', requireAll: false })

    const orders = computed(() => maintenanceStore.serviceOrders.items)
    const userOptions = computed(() =>
      accountsStore.all().filter(u => u.name).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    )
    const userNameOf = (username: string) => userOptions.value.find(u => u.username === username)?.name || username

    const typeGroups = computed<OsTypeGroup[]>(() => {
      const map = new Map<string, OsTypeGroup>()
      for (const os of orders.value) {
        const rootId = os.sourceOsId || os.id
        let group = map.get(rootId)
        if (!group) {
          group = { rootId, title: os.title, instances: [], lastCompletedAt: '', lastCompletedByName: '', newestHasPending: false, newestApproved: false }
          map.set(rootId, group)
        }
        group.instances.push(os)
        if (os.lastCompletedAt && os.lastCompletedAt > group.lastCompletedAt) {
          group.lastCompletedAt = os.lastCompletedAt
          group.lastCompletedByName = os.lastCompletedByName
        }
      }
      const groups = Array.from(map.values())
      groups.forEach(g => {
        g.instances.sort((a, b) => a.osNumber - b.osNumber)
        const active = [...g.instances].reverse().find(i => i.status !== 'cancelled') || g.instances[g.instances.length - 1]
        g.newestHasPending = Boolean(active?.hasPending)
        g.newestApproved = active?.status === 'approved'
      })
      return groups.sort((a, b) => a.title.localeCompare(b.title))
    })

    const statusLabel = (s: string) => ({ pending: 'Em andamento', approved: 'Aprovada', cancelled: 'Cancelada' }[s] || s)
    const formatDateTime = (iso: string) => iso ? new Date(iso).toLocaleString('pt-BR') : ''

    const reload = async () => {
      loading.value = true
      try {
        await maintenanceStore.loadServiceOrders({ projectId: props.project.id })
      } finally {
        loading.value = false
      }
    }

    const addResponsavel = () => {
      if (respPick.value && !createForm.responsavelUserIds.includes(respPick.value)) {
        createForm.responsavelUserIds.push(respPick.value)
      }
      respPick.value = ''
    }
    const removeResponsavel = (username: string) => {
      createForm.responsavelUserIds = createForm.responsavelUserIds.filter(u => u !== username)
    }
    const addCreateItem = () => { createForm.items.push({ title: '', description: '' }) }
    const removeCreateItem = (idx: number) => { createForm.items.splice(idx, 1) }

    const submitCreate = async () => {
      createError.value = ''
      const items = createForm.items.filter(it => it.title.trim())
      if (!createForm.title.trim()) { createError.value = 'Informe o título da OS.'; return }
      if (!createForm.responsavelUserIds.length) { createError.value = 'Selecione ao menos um responsável.'; return }
      if (!items.length) { createError.value = 'Adicione ao menos uma rotina.'; return }
      creating.value = true
      try {
        await maintenanceStore.createServiceOrder({
          projectId: props.project.id,
          projectName: props.project.name,
          title: createForm.title.trim(),
          description: createForm.description.trim(),
          priority: createForm.priority,
          dueDate: createForm.dueDate,
          responsavelUserIds: createForm.responsavelUserIds,
          items
        })
        createForm.title = ''
        createForm.description = ''
        createForm.priority = 'normal'
        createForm.dueDate = ''
        createForm.responsavelUserIds = []
        createForm.items = [{ title: '', description: '' }]
        showCreateForm.value = false
        await reload()
      } catch (err) {
        createError.value = err instanceof Error ? err.message : 'Erro ao requisitar OS.'
      } finally {
        creating.value = false
      }
    }

    const toggleEditType = (group: OsTypeGroup) => {
      if (editingType.value === group.rootId) { editingType.value = ''; return }
      editingType.value = group.rootId
      const root = group.instances.find(i => i.id === group.rootId) || group.instances[0]
      editTypeForm.title = root.title
      editTypeForm.description = root.description
      editTypeForm.priority = root.priority
      editTypeForm.dueDate = root.dueDate
    }
    const submitEditType = async (rootId: string) => {
      await maintenanceStore.updateServiceOrder(rootId, { ...editTypeForm })
      editingType.value = ''
      await reload()
    }

    const toggleDesignateType = (rootId: string) => {
      if (designatingType.value === rootId) { designatingType.value = ''; return }
      designatingType.value = rootId
      designateError.value = ''
      designateResult.value = ''
      designateForm.responsavelUserIds = []
      designateForm.dueDate = ''
      designateForm.requireAll = false
    }
    const addDesignateResponsavel = () => {
      if (designatePick.value && !designateForm.responsavelUserIds.includes(designatePick.value)) {
        designateForm.responsavelUserIds.push(designatePick.value)
      }
      designatePick.value = ''
    }
    const removeDesignateResponsavel = (username: string) => {
      designateForm.responsavelUserIds = designateForm.responsavelUserIds.filter(u => u !== username)
    }
    const submitDesignate = async (rootId: string) => {
      designateError.value = ''
      designateResult.value = ''
      if (!designateForm.responsavelUserIds.length) { designateError.value = 'Selecione ao menos um responsável.'; return }
      designating.value = true
      try {
        const result = await maintenanceStore.designateServiceOrder(rootId, { ...designateForm })
        const numbers = (result.osNumbers || []).map(n => `OS${n}`).join(', ')
        designateResult.value = `Criada(s): ${numbers}.`
        designateForm.responsavelUserIds = []
        designateForm.dueDate = ''
        designateForm.requireAll = false
        await reload()
      } catch (err) {
        designateError.value = err instanceof Error ? err.message : 'Erro ao requisitar OS.'
      } finally {
        designating.value = false
      }
    }

    onMounted(async () => {
      if (accountsStore.all().length === 0) await accountsStore.refresh()
      await reload()
    })

    return {
      loading, selectedOsId, typeGroups, reload,
      showCreateForm, creating, createError, respPick, createForm, userOptions, userNameOf,
      addResponsavel, removeResponsavel, addCreateItem, removeCreateItem, submitCreate,
      editingType, editTypeForm, toggleEditType, submitEditType,
      designatingType, designatePick, designating, designateError, designateResult, designateForm,
      toggleDesignateType, addDesignateResponsavel, removeDesignateResponsavel, submitDesignate,
      statusLabel, formatDateTime
    }
  }
})
</script>

<style scoped lang="scss">
.mot-panel { display: flex; flex-direction: column; gap: 16px; }
.mot-top { display: flex; flex-direction: column; gap: 12px; }

.mot-new-form {
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  padding: 16px;
  display: flex; flex-direction: column; gap: 14px;
}
.mot-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
.mot-field { display: flex; flex-direction: column; gap: 4px; }
.mot-field-wide { grid-column: 1 / -1; }
.mot-label { font-size: 12px; font-weight: 600; color: var(--ink-muted); }
.mot-hint { font-weight: 400; opacity: .8; }
.mot-check-row { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--ink); }

.mot-input, .mot-select, .mot-textarea {
  border: 1px solid rgba(31,30,26,.18); border-radius: 8px; padding: 8px 10px;
  font-size: 13px; color: var(--ink); background: var(--bg, #fff); font-family: inherit;
}
.mot-textarea { resize: vertical; }

.mot-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.mot-chip {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(31,30,26,.06); border-radius: 999px; padding: 4px 6px 4px 12px; font-size: 12px;
}
.mot-chip-x { border: none; background: none; cursor: pointer; color: var(--ink-muted); font-size: 14px; line-height: 1; padding: 2px 4px; }

.mot-items-form { display: flex; flex-direction: column; gap: 8px; }
.mot-section-label { font-size: 13px; font-weight: 700; color: var(--ink); }
.mot-item-row { display: grid; grid-template-columns: 1fr 1fr auto; gap: 8px; align-items: center; }

.mot-error { color: #dc2626; font-size: 12px; margin: 0; }
.mot-success { color: #16a34a; font-size: 12px; margin: 0; }
.mot-form-actions { display: flex; justify-content: flex-end; gap: 8px; }

.mot-btn {
  border: none; border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 600; cursor: pointer;
  background: rgba(31,30,26,.08); color: var(--ink);
  &:disabled { opacity: .6; cursor: default; }
}
.mot-btn-primary { background: var(--accent); color: #fff; }

.mot-loading, .mot-empty { font-size: 13px; color: var(--ink-muted); padding: 24px 0; text-align: center; }

.mot-list { display: flex; flex-direction: column; gap: 12px; }
.mot-type-card {
  background: var(--card, #fff); border: 1.5px solid rgba(31,30,26,.12); border-radius: 14px;
  padding: 14px 16px; display: flex; flex-direction: column; gap: 10px;
}
.mot-type-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.mot-type-heading { display: flex; flex-direction: column; gap: 2px; }
.mot-type-title { font-size: 16px; font-weight: 700; color: var(--ink); }
.mot-type-sub { font-size: 12px; color: var(--ink-muted); }
.mot-type-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.mot-type-status { font-size: 12px; color: var(--ink-muted); display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.mot-flag { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 999px; background: rgba(31,30,26,.08); color: var(--ink-muted); }
.mot-flag-pending { background: rgba(217,119,6,.12); color: #d97706; }
.mot-flag-ok { background: rgba(22,163,74,.12); color: #16a34a; }

.mot-type-instances { display: flex; flex-wrap: wrap; gap: 8px; }
.mot-instance-chip {
  display: inline-flex; align-items: center; gap: 6px;
  border: 1px solid rgba(31,30,26,.15); border-radius: 999px;
  padding: 5px 6px 5px 12px; font-size: 12px; font-weight: 600; color: var(--ink);
  background: var(--bg, #fff); cursor: pointer;
  &:hover { border-color: var(--accent); }
}
.mot-status-badge {
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700;
  &.s-pending { background: rgba(217,119,6,.12); color: #d97706; }
  &.s-approved { background: rgba(22,163,74,.12); color: #16a34a; }
  &.s-cancelled { background: rgba(100,116,139,.12); color: #475569; }
}
</style>
